/**
 * MobileNav — Navigation auf dem Handy (unter md). Fixe Bottom-Bar mit den
 * wichtigsten rollen-relevanten Zielen + „Mehr" (⋯), das ein Bottom-Sheet mit
 * ALLEN Zielen und den Konto-Aktionen öffnet. Ersetzt die alte BottomNavigation.
 * Auf Desktop ausgeblendet (dort übernimmt die Sidebar).
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigateGeschuetzt } from '@/hooks/useVerlassenWarnung';
import { useAuth } from '@/context/AuthContext';
import { NAV_ITEMS, filterNavByRole, filterNavByRechte, isNavActive, parseRoles } from '@/config/navigation';
import { useZugang } from '@/hooks/useZugang';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { MoreHorizontal, User, Settings, LogOut } from 'lucide-react';

export function MobileNav() {
  const location = useLocation();
  const navigate = useNavigateGeschuetzt();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const { rechte, geladen, laedt, fachbereich } = useZugang();
  const items = filterNavByRechte(filterNavByRole(NAV_ITEMS, user?.role), rechte, geladen, fachbereich);
  // Beim Kaltstart nichts anbieten: die Leiste behaelt ihre Hoehe, aber es
  // blitzen keine Ziele auf, die gleich wieder verschwinden. Ein Fehlschlag
  // ist kein Ladezustand — dann steht `items` wie bisher voll da.
  const hasOverflow = !laedt && items.length > 5;
  const primary = laedt ? [] : hasOverflow ? items.slice(0, 4) : items;
  const roles = parseRoles(user?.role);
  const isGf = roles.includes('geschaeftsfuehrung');

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 safe-area-bottom">
      <div className="flex justify-around items-stretch" aria-busy={laedt}>
        {laedt &&
          Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex-1 flex items-center justify-center min-h-[56px] px-1" aria-hidden>
              <div className="h-6 w-6 rounded-full bg-muted animate-pulse" />
            </div>
          ))}
        {primary.map((item) => {
          const active = isNavActive(location.pathname, item.path);
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] px-1 transition-colors ${
                active ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <Icon className="w-6 h-6" />
              <span className="text-[10px] font-medium truncate max-w-full">{item.label}</span>
            </button>
          );
        })}

        {hasOverflow && (
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] px-1 text-muted-foreground"
                aria-label="Mehr"
              >
                <MoreHorizontal className="w-6 h-6" />
                <span className="text-[10px] font-medium">Mehr</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto">
              <SheetHeader className="text-left mb-3">
                <SheetTitle>Menü</SheetTitle>
              </SheetHeader>

              {/* Alle Ziele */}
              <div className="grid grid-cols-3 gap-2">
                {items.map((item) => {
                  const active = isNavActive(location.pathname, item.path);
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => go(item.path)}
                      className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-colors ${
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:bg-muted text-foreground'
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-[11px] font-medium leading-tight">{item.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Konto */}
              <div className="mt-4 pt-4 border-t border-border space-y-1">
                <button onClick={() => go('/profil')} className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-muted">
                  <User className="w-5 h-5" /> Profil
                </button>
                {isGf && (
                  <button onClick={() => go('/settings/questions')} className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-muted">
                    <Settings className="w-5 h-5" /> Einstellungen
                  </button>
                )}
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="w-5 h-5" /> Abmelden
                </button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </nav>
  );
}
